import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import { useJunkYard } from '../contexts/junk-yard-context';
import type { JunkYard } from '../models/junk-yard';
import { isSSR } from '../utils/is-ssr';

type InitialFetch<T> = () => Promise<T>;

interface IFetcher<T> {
  initialFetch: InitialFetch<T>;
}

function getMemo<T>(memoId: string, ssrMemo: JunkYard['ssrMemo']): T | null {
  return ssrMemo ? (ssrMemo[memoId] as T) : null;
}

function setMemo<T>(memoId: string, data: T, ssrMemo: JunkYard['ssrMemo']) {
  if (ssrMemo) {
    ssrMemo[memoId] = data;
  }
}

type GetFetcher<T> = ({ initialFetch }: { initialFetch: InitialFetch<T> }) => T;

function getFetcher<T>(memoId: string): GetFetcher<T> {
  let data: T | undefined;
  let promise: Promise<T> | undefined;
  let error: Error;

  return ({ initialFetch }: { initialFetch: InitialFetch<T> }) => {
    if (data) {
      return data;
    }

    if (error) {
      throw new Error(
        `Fetcher with memo id ${memoId} failed to fetch data with error: ${error}`,
      );
    }

    if (!promise) {
      promise = initialFetch()
        .then((res) => {
          data = res;
          return res;
        })
        .catch((e: Error) => {
          error = e;
          throw e;
        });
    }

    throw promise;
  };
}

interface IFetcherHOCProps<T> extends IFetcher<T> {
  fetcher: GetFetcher<T>;
  memoId: string;
  children: (props: { data: T }) => ReactElement;
}

function FetcherHOC<T>({
  fetcher,
  initialFetch,
  memoId,
  children: Children,
}: IFetcherHOCProps<T>) {
  const { ssrMemo } = useJunkYard();

  const getData = () => {
    if (!isSSR()) {
      return getMemo<T>(memoId, ssrMemo) || fetcher({ initialFetch });
    }
    return fetcher({ initialFetch });
  };

  const data = getData();

  if (isSSR()) {
    setMemo(memoId, data, ssrMemo);
  }

  return <Children data={data} />;
}

interface IWithFetcherProps<T> extends IFetcher<T> {
  loader: ReactElement;
  memoId?: string;
  children: (props: { data: T }) => ReactElement;
}

/**
 * Fetcher decorator logic for fetching data on SSR & CSR.
 * Refactored version of with-fetcher of marketplace-component, with types & functional components.
 * Suspense is used for CSR data fetching.
 */
export function FetcherDecorator<T>({
  initialFetch,
  memoId = '',
  children,
  loader,
}: IWithFetcherProps<T>) {
  const fetcher = useMemo(() => getFetcher<T>(memoId), [memoId]);

  return (
    <React.Suspense fallback={loader}>
      <FetcherHOC fetcher={fetcher} initialFetch={initialFetch} memoId={memoId}>
        {children}
      </FetcherHOC>
    </React.Suspense>
  );
}
